/**
 * Screen Recorder Hook
 *
 * Hook for recording screen/window/tab using the MediaRecorder API.
 * Supports audio from microphone and/or system audio.
 */

import { useState, useRef, useCallback, useEffect } from "react";

export interface UseScreenRecorderOptions {
  audio?: boolean;
  video?: {
    width?: number;
    height?: number;
    frameRate?: number;
  };
  mimeType?: string;
}

export interface UseScreenRecorderReturn {
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  previewStream: MediaStream | null;
  isSupported: boolean;
  error: string | null;
  startRecording: () => Promise<boolean>;
  stopRecording: () => Promise<Blob | null>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  cancelRecording: () => void;
}

const DEFAULT_OPTIONS: UseScreenRecorderOptions = {
  audio: true,
  video: {
    width: 1920,
    height: 1080,
    frameRate: 30,
  },
  mimeType: "video/webm;codecs=vp9",
};

export function useScreenRecorder(
  options: UseScreenRecorderOptions = {}
): UseScreenRecorderReturn {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);

  // Check if screen recording is supported
  const isSupported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    !!navigator.mediaDevices.getDisplayMedia;

  // Get best supported MIME type
  const getSupportedMimeType = useCallback((): string => {
    const types = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8,opus",
      "video/webm;codecs=vp8",
      "video/webm",
      "video/mp4",
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return "";
  }, []);

  // Start recording timer
  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now() - pausedTimeRef.current * 1000;
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      setRecordingTime(elapsed);
    }, 100);
  }, []);

  // Stop recording timer
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cleanup streams
  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setPreviewStream(null);
  }, []);

  // Start recording
  const startRecording = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError("Screen recording is not supported in this browser");
      return false;
    }

    try {
      setError(null);
      chunksRef.current = [];

      // Get screen stream
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: mergedOptions.video?.width },
          height: { ideal: mergedOptions.video?.height },
          frameRate: { ideal: mergedOptions.video?.frameRate },
        },
        audio: true, // Try to get system audio
      });

      // Get microphone stream if audio is enabled
      let combinedStream = displayStream;

      if (mergedOptions.audio) {
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });

          // Combine video from display with audio from microphone
          const audioTrack = audioStream.getAudioTracks()[0];
          if (audioTrack) {
            combinedStream = new MediaStream([
              ...displayStream.getVideoTracks(),
              audioTrack,
              // Also include display audio if available
              ...displayStream.getAudioTracks(),
            ]);
          }
        } catch (audioErr) {
          console.warn("Could not get microphone access:", audioErr);
          // Continue without microphone
        }
      }

      streamRef.current = combinedStream;
      setPreviewStream(combinedStream);

      // Set up MediaRecorder
      const mimeType = getSupportedMimeType();
      if (!mimeType) {
        throw new Error("No supported video format found");
      }

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: 2500000, // 2.5 Mbps
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Handle stream ending (user clicks "Stop sharing")
      displayStream.getVideoTracks()[0].onended = () => {
        if (isRecording) {
          stopRecording();
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect data every second

      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      pausedTimeRef.current = 0;
      startTimer();

      return true;
    } catch (err: any) {
      console.error("[useScreenRecorder] Error starting recording:", err);

      if (err.name === "NotAllowedError") {
        setError("Screen recording permission denied");
      } else if (err.name === "NotFoundError") {
        setError("No screen to record was selected");
      } else {
        setError(err.message || "Failed to start recording");
      }

      cleanupStream();
      return false;
    }
  }, [isSupported, mergedOptions, getSupportedMimeType, startTimer, cleanupStream, isRecording]);

  // Stop recording
  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (!mediaRecorder || mediaRecorder.state === "inactive") {
        cleanupStream();
        setIsRecording(false);
        setIsPaused(false);
        stopTimer();
        resolve(null);
        return;
      }

      mediaRecorder.onstop = () => {
        const mimeType = getSupportedMimeType();
        const blob = new Blob(chunksRef.current, { type: mimeType });

        cleanupStream();
        setIsRecording(false);
        setIsPaused(false);
        stopTimer();

        chunksRef.current = [];
        mediaRecorderRef.current = null;

        resolve(blob);
      };

      mediaRecorder.stop();
    });
  }, [cleanupStream, stopTimer, getSupportedMimeType]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state !== "recording") return;

    mediaRecorder.pause();
    setIsPaused(true);
    stopTimer();
    pausedTimeRef.current = recordingTime;
  }, [recordingTime, stopTimer]);

  // Resume recording
  const resumeRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state !== "paused") return;

    mediaRecorder.resume();
    setIsPaused(false);
    startTimer();
  }, [startTimer]);

  // Cancel recording (stop without saving)
  const cancelRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;

    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.onstop = () => {
        // Don't create blob, just cleanup
        chunksRef.current = [];
        mediaRecorderRef.current = null;
      };
      mediaRecorder.stop();
    }

    cleanupStream();
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    stopTimer();
  }, [cleanupStream, stopTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupStream();
      stopTimer();
    };
  }, [cleanupStream, stopTimer]);

  return {
    isRecording,
    isPaused,
    recordingTime,
    previewStream,
    isSupported,
    error,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
  };
}

export default useScreenRecorder;
